import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';
import AddProductModal from './AddProductModal';
import MerchantStockAlerts from './MerchantStockAlerts';

const { FiPackage, FiTrash2, FiEye, FiEyeOff, FiRefreshCw, FiClock, FiCheck, FiX, FiEdit2, FiPlus } = FiIcons;

const PRODUCTS_TABLE = 'merchant_products_1776540001';

const STATUS_STYLES = {
  pending:  'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const ProductsTab = () => {
  const user = getSession();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const fetchProducts = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    let loaded = false;
    try {
      const res = await writeFetch(`read/${PRODUCTS_TABLE}?owner_id=${user.id}&limit=100&sort=created_at&order=desc`);
      const data = await res.json();
      if (data.status === 'success' && data.data && data.data.length > 0) {
        setProducts(data.data);
        loaded = true;
      }
    } catch { /* silent */ }

    if (!loaded) {
      const local = JSON.parse(localStorage.getItem(`mock_products_${user.id}`) || '[]');
      setProducts(local);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggleActive = async (product) => {
    const newVal = product.is_active ? 0 : 1;
    try {
      await writeFetch(`update/${PRODUCTS_TABLE}/${product.id}`, {
        method: 'PUT', body: JSON.stringify({ is_active: newVal }),
      });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_active: newVal } : p));
      
      const local = JSON.parse(localStorage.getItem(`mock_products_${user.id}`) || '[]');
      const updatedLocal = local.map(p => p.id === product.id ? { ...p, is_active: newVal } : p);
      localStorage.setItem(`mock_products_${user.id}`, JSON.stringify(updatedLocal));
    } catch { /* silent */ }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await writeFetch(`delete/${PRODUCTS_TABLE}/${product.id}`, { method: 'DELETE' });
      setProducts(prev => prev.filter(p => p.id !== product.id));

      const local = JSON.parse(localStorage.getItem(`mock_products_${user.id}`) || '[]');
      const updatedLocal = local.filter(p => p.id !== product.id);
      localStorage.setItem(`mock_products_${user.id}`, JSON.stringify(updatedLocal));
    } catch { /* silent */ }
  };

  const handleEditProduct = (product) => {
    // Set the product data for editing
    setEditingProduct(product);
    setIsAddOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-3 border border-brand-dark rounded-2xl shadow-sm">
        <div className="px-4 text-[10px] font-black uppercase tracking-widest text-text-muted">
          My Products ({products.length})
        </div>
        <div className="flex gap-2">
          <button onClick={fetchProducts} className="p-3 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:border-accent-gold transition-all">
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          </button>
          <button onClick={() => { setEditingProduct(null); setIsAddOpen(true); }}
            className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-lg">
            <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Product
          </button>
        </div>
      </div>

      {!loading && <MerchantStockAlerts products={products} onReview={handleEditProduct} />}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="col-span-full py-20 flex flex-col items-center bg-white rounded-[40px] border-2 border-dashed border-brand-dark">
          <div className="w-16 h-16 bg-brand-light rounded-3xl flex items-center justify-center mb-4">
            <SafeIcon icon={FiPackage} className="w-8 h-8 text-text-muted" />
          </div>
          <p className="text-xl font-serif text-accent-navy">No products yet</p>
          <p className="text-sm text-text-muted mt-2 font-light">Use the "Add Product" button to create your first item.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {products.map(p => {
             const status = p.approval_status || 'pending';
             return (
               <div key={p.id} className="bg-white rounded-[28px] border border-brand-dark overflow-hidden group hover:border-accent-gold transition-all shadow-sm">
                 <div className="h-40 bg-brand-light relative overflow-hidden">
                   <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                     onError={e => { e.target.src='https://images.unsplash.com/photo-1563241527-3004b7be0ffd?q=80&w=800'; }} />
                   
                   <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-accent-navy shadow-sm">
                     ${parseFloat(p.price).toFixed(2)}
                   </div>

                   <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-[9px] font-black uppercase border ${STATUS_STYLES[status]}`}>
                     {status === 'pending' ? <><SafeIcon icon={FiClock} className="inline w-2.5 h-2.5 mr-1" />Pending</> :
                      status === 'approved' ? <><SafeIcon icon={FiCheck} className="inline w-2.5 h-2.5 mr-1" />Approved</> :
                      <><SafeIcon icon={FiX} className="inline w-2.5 h-2.5 mr-1" />Rejected</>}
                   </div>

                   {!p.is_active && (
                     <div className="absolute bottom-3 left-3 bg-red-500 text-white px-2 py-1 rounded-full text-[9px] font-black uppercase">Hidden</div>
                   )}
                 </div>
                 <div className="p-5">
                   <h4 className="font-bold text-accent-navy text-sm mb-1 truncate">{p.name}</h4>
                   <p className="text-[10px] text-text-muted uppercase tracking-widest mb-4 truncate">{p.category}</p>
                   <div className="flex gap-2">
                     <button onClick={() => toggleActive(p)}
                       className="flex-1 py-2 bg-brand-light rounded-xl text-text-muted hover:bg-accent-navy hover:text-white transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest">
                       <SafeIcon icon={p.is_active ? FiEyeOff : FiEye} /> {p.is_active ? 'Hide' : 'Show'}
                     </button>
                     <button onClick={() => handleEditProduct(p)}
                       className="p-2 bg-brand-light text-text-muted rounded-xl hover:bg-accent-gold hover:text-accent-navy transition-all">
                       <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                     </button>
                     <button onClick={() => deleteProduct(p)}
                       className="p-2 bg-brand-light text-text-muted rounded-xl hover:bg-red-50 hover:text-red-500 transition-all">
                       <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                     </button>
                   </div>
                 </div>
               </div>
             );
          })}
        </div>
      )}
      <AddProductModal isOpen={isAddOpen} onClose={() => { setIsAddOpen(false); setEditingProduct(null); }} onSuccess={fetchProducts} editData={editingProduct} />
    </div>
  );
};

export default ProductsTab;